import { Injectable } from '@angular/core';
import { IDeviceModel } from '@nx-fe/services';
import { v4 as uuid } from 'uuid';


export interface IWorkPanelState {
    status: WorkPanelStatus;
    type: WorkPanelType;
    device?: IDeviceModel; // current device shown in the work panel, mainly for switching between rc and snapshot
    customProperty: { [key: string]: any };
}

interface IWorkPanelStateDictionary {
    [key: string]: IWorkPanelState;
}

export interface IWorkPanelSyncData {
    workPanels: IWorkPanelStateDictionary;
    currentWorkPanelKey: string;
}

export enum WorkPanelType {
    filePreview = 1,
    savedSnapshot = 2,
    liveSnapshot = 3,
    rc = 4
}

export enum WorkPanelStatus {
    embedded = 1,
    poppedOut = 2,
    minimized = 3,
    standalone = 4
}

@Injectable({
    providedIn: 'root'
})
export class WorkPanelManagerService {

    private workPanelDict: IWorkPanelStateDictionary = {};

    constructor() {
    }

    public get workPanels(): IWorkPanelStateDictionary {
        return this.workPanelDict;
    }

    public set workPanels(value: IWorkPanelStateDictionary) {
        this.workPanelDict = value;
    }

    public addWorkPanel = (panelType: WorkPanelType, status: WorkPanelStatus, device?: IDeviceModel, customProperty?: { [key: string]: any }): string => {
        if (!panelType) {
            console.error('Adding work panel failed: no panel type specified');
            return null;
        }

        // only one embedded or popOut work panel should exist at a given time
        this.removeActiveWorkPanelsByStatus(status);

        const key = uuid();
        const newWorkPanel = {
            status,
            type: panelType,
            device,
            customProperty: customProperty || null
        };

        this.workPanelDict[key] = newWorkPanel;

        // this key should be only used within the component that created it and its child components
        return key;
    };

    public removeWorkPanel = (key: string): void => {
        if (!key || !this.workPanelDict[key]) {
            return;
        }

        delete this.workPanelDict[key];
    };

    public getPanelType = (key: string): WorkPanelType => {
        if (!this.workPanelDict[key]) {
            return null;
        }

        return this.workPanelDict[key].type;
    };

    public getPanelDevice = (key: string): IDeviceModel => {
        if (!this.workPanelDict[key]) {
            return null;
        }
        return this.workPanelDict[key].device;
    };

    public getPanelCustomProperty = (key: string): { [key: string]: any } => {
        if (!this.workPanelDict[key]) {
            return null;
        }
        return this.workPanelDict[key].customProperty;
    };

    /**
     * There should be only one embedded panel at a time, still looping through just in case
     */
    public removeActiveWorkPanelsByStatus = (status: WorkPanelStatus): void => {
        for (const key in this.workPanelDict) {
            if (this.workPanelDict[key].status === status) {
                delete this.workPanelDict[key];
            }
        }
    };

    public isPanelTypeEmbedded = (panelType: number): boolean => {
        for (const key in this.workPanelDict) {
            if (this.workPanelDict[key].status === WorkPanelStatus.embedded &&
                this.workPanelDict[key].type === panelType) {
                return true;
            }
        }
        return false;
    };

    public isPanelTypeMinimized = (panelType: number): boolean => {
        for (const key in this.workPanelDict) {
            if (this.workPanelDict[key].status === WorkPanelStatus.minimized &&
                this.workPanelDict[key].type === panelType) {
                return true;
            }
        }
        return false;
    };

    public isDeviceUsedByPanelType = (device: IDeviceModel, panelType: number): boolean => {
        if (!device) {
            return null;
        }
        for (const key in this.workPanelDict) {
            if (this.workPanelDict[key].type === panelType && this.workPanelDict[key].device) {
                return this.workPanelDict[key].device.deviceId === device.deviceId;
            }
        }
        return false;
    };

    public isPanelTypePoppedOut = (panelType: number): boolean => {
        for (const key in this.workPanelDict) {
            if (this.workPanelDict[key].status === WorkPanelStatus.poppedOut &&
                this.workPanelDict[key].type === panelType) {
                return true;
            }
        }
        return false;
    };

    public isPanelPoppedOut = (key: string): boolean => {
        if (!this.workPanelDict[key]) {
            return false;
        }

        return this.workPanelDict[key].status === WorkPanelStatus.poppedOut;
    };

    public canPanelBePoppedOut = (panelType: WorkPanelType) => panelType === WorkPanelType.rc
            || panelType === WorkPanelType.liveSnapshot;

    public changePanelStatus = (key: string, status: WorkPanelStatus): string => {
        if (!this.workPanelDict[key]) {
            return null;
        }
        if (this.workPanelDict[key].status === status) {
            return key;
        }

        // there should be one panel for a given status
        this.removeActiveWorkPanelsByStatus(status);
        this.workPanelDict[key].status = status;

        return key;
    };

    public getPanelStatus = (panelType: number): WorkPanelStatus => {
        for (const key in this.workPanelDict) {
            if (this.workPanelDict[key].type === panelType) {
                return this.workPanelDict[key].status;
            }
        }
        return null;
    };

    public clear = () => {
        this.workPanelDict = {};
    };
}
